import { type FC, useState, type ReactNode } from "react";

import { ButtonGroup } from "@hightouch/htui";
import { isEqual } from "lodash";
import { singular } from "pluralize";
import type { Schema } from "yup";

import { IntegrationIcon } from "src/components/integrations/integration-icon";
import { FieldError } from "src/components/field-error";

import airtable from "./airtable";
import makeFileUploadForm from "./file-upload";
import googlesheets from "./google-sheets";
import metabase from "./metabase";
import microsoftexcel from "./microsoft-excel";
import mixpanel from "./mixpanel";
import mongodb from "./mongodb";
import sftp from "./sftp";
import tableau from "./tableau";
import {
  QueryContainer,
  QueryContent,
  QueryHeader,
  QueryHeaderTitle,
} from "src/components/query";

type Source = {
  id: string;
  name: string;
  type: string;
  definition: {
    name: string;
    icon: string;
  };
  config: Record<string, any>;
};

export type CustomQuery = {
  type?: string;
  path?: string;
  fieldSeparator?: string;
  fileType?: "automatic" | "json" | "ndjson" | "csv" | "parquet";
  projectId?: number;
  baseId?: string;
  workbookId?: string;
  viewId?: string;
  sheetId?: string;
  readAllObjectsAtPath?: boolean;
} & Record<string, unknown>;

export type Query = {
  QueryForm: FC<Readonly<CustomQueryFormProps>>;
  QueryView: FC<Readonly<CustomQueryViewProps>>;
  querySchema: Schema<CustomQuery, object>;
};

export type CustomQueryFormProps = {
  source: Source;
  query: CustomQuery | undefined;
  onChange: (query: CustomQuery) => void;
  error: Error | null;
  setError: (error: Error | null) => void;
};

export type CustomQueryViewProps = {
  source: Source;
  query: CustomQuery;
  error: Error | null;
  setError: (error: Error | null) => void;
};

const QUERIES: { [key: string]: Query } = {
  airtable,
  googlesheets,
  mixpanel,
  metabase,
  mongodb,
  "azure-blob": makeFileUploadForm(),
  s3: makeFileUploadForm({
    objectLabel: "S3 object",
    enableUseLastModified: true,
    enableReadAllObjectsAtPath: true,
  }),
  gcs: makeFileUploadForm(),
  sftp,
  tableau,
  microsoftexcel,
};

export const CustomQueryForm: FC<
  Readonly<{
    source: Source;
    query?: CustomQuery;
    onChange: (query: CustomQuery) => void;
  }>
> = ({ source, query, onChange }) => {
  const [error, setError] = useState<Error | null>(null);
  const Form = QUERIES[source.type]?.QueryForm;

  return (
    <QueryContainer>
      <QueryHeader>
        <QueryHeaderTitle
          icon={
            <IntegrationIcon
              src={source?.definition.icon}
              name={source?.definition.name}
            />
          }
          title={source?.name ?? "Private source"}
        />
      </QueryHeader>
      <QueryContent>
        {Form && (
          <Form
            error={error}
            query={query}
            setError={setError}
            source={source}
            onChange={onChange}
          />
        )}
        <FieldError error={error} />
      </QueryContent>
    </QueryContainer>
  );
};

export const CustomQueryView: FC<
  Readonly<{ source: Source; query: CustomQuery; actions?: ReactNode }>
> = ({ source, query, actions }) => {
  const View = QUERIES[source.type]?.QueryView;
  const [error, setError] = useState<Error | null>(null);

  return (
    <QueryContainer>
      <QueryHeader>
        <QueryHeaderTitle
          icon={
            <IntegrationIcon
              src={source?.definition?.icon}
              name={source?.definition?.name}
            />
          }
          title={singular(source?.definition?.name) ?? "Private source"}
        />

        {actions && <ButtonGroup>{actions}</ButtonGroup>}
      </QueryHeader>
      <QueryContent>
        {View && (
          <View
            error={error}
            query={query}
            setError={setError}
            source={source}
          />
        )}
        <FieldError error={error} />
      </QueryContent>
    </QueryContainer>
  );
};

export async function validateCustomQuery(
  source: Source,
  query: CustomQuery | undefined,
): Promise<void> {
  const definition = QUERIES[source.type];

  if (definition) {
    await definition.querySchema.validate(query, { abortEarly: false });
  }
}

export function isCustomQueryDirty(
  state: CustomQuery | undefined,
  stored: CustomQuery | undefined,
): boolean {
  return !isEqual(state, stored);
}
